
import React, { useEffect, useState } from 'react';
import { User, Subject, EducationLevel } from '../types';
import { BOARD_OPTIONS, getBoardsForSubject } from '../data/examBoards';
import { Mail, Atom, Lock, Loader2, AlertCircle, Phone, Check, ChevronRight, BookOpen, GraduationCap, ChevronLeft, User as UserIcon, Building2, Compass } from 'lucide-react';
import { loginUser, registerUser, googleLogin, setCurrentUser, handleGoogleRedirect } from '../services/auth';

interface AuthProps {
  onLogin: (user: User) => void;
  initialMode?: 'sign-in' | 'sign-up';
}

// Registration Steps
enum RegStep {
  Credentials = 0,
  Level = 1,
  Subjects = 2,
  ExamBoards = 3
}

const Auth: React.FC<AuthProps> = ({ onLogin, initialMode = 'sign-in' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'sign-in');

  // Login State
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register State
  const [regStep, setRegStep] = useState<RegStep>(RegStep.Credentials);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regLevel, setRegLevel] = useState<EducationLevel | null>(null);
  const [regSubjects, setRegSubjects] = useState<Subject[]>([]);
  const [regBoards, setRegBoards] = useState<Record<string, string>>({});

  // Google onboarding state
  const [googleUser, setGoogleUser] = useState<any>(null);
  const [isGoogleOnboarding, setIsGoogleOnboarding] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsLogin(initialMode === 'sign-in');
  }, [initialMode]);

  // Check for Google redirect result on mount
  useEffect(() => {
    const checkRedirect = async () => {
      try {
        setLoading(true);
        const user = await handleGoogleRedirect();
        if (user) {
          // Check if user needs onboarding (new Google user without subjects/level)
          if ((user as any).needsOnboarding) {
            setGoogleUser(user);
            setRegName(user.name || '');
            setRegEmail(user.email || '');
            setIsGoogleOnboarding(true);
            setIsLogin(false);
            setRegStep(RegStep.Level);
          } else {
            onLogin(user);
          }
        }
      } catch (err) {
        console.error('Redirect check error:', err);
      } finally {
        setLoading(false);
      }
    };
    checkRedirect();
  }, [onLogin]);

  // --- Handlers ---

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await loginUser(loginEmail, loginPassword);
      setCurrentUser(user);
      onLogin(user);
    } catch (err: any) {
      console.error('Login error:', err);
      const message = err?.message || 'Login failed';
      if (message.includes('Invalid credentials') || message.includes('401')) {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    setError('');
    if (regStep === RegStep.Credentials) {
        if (!regName.trim() || !regEmail || !regPassword) {
            setError('Please fill in your name, email and password.');
            return;
        }
        setRegStep(RegStep.Level);
    } else if (regStep === RegStep.Level) {
        if (!regLevel) {
            setError('Please select a level.');
            return;
        }
        // University and Independent learners skip subject/board selection - blank canvas
        if (regLevel === 'University' || regLevel === 'Independent') {
            // Skip directly to registration with empty subjects
            setRegSubjects([]);
            setRegBoards({});
            return; // Will trigger registration via the button
        }
        setRegStep(RegStep.Subjects);
    } else if (regStep === RegStep.Subjects) {
        if (regSubjects.length === 0) {
            setError('Please select at least one subject.');
            return;
        }
        setRegStep(RegStep.ExamBoards);
    }
  };

  const handlePrevStep = () => {
      // For Google onboarding, don't allow going back to Credentials step
      if (isGoogleOnboarding && regStep === RegStep.Level) {
          // Cancel Google onboarding and return to login
          setIsGoogleOnboarding(false);
          setGoogleUser(null);
          setIsLogin(true);
          setRegStep(RegStep.Credentials);
          return;
      }
      if (regStep > 0) setRegStep(regStep - 1);
  };

  const handleRegisterSubmit = async () => {
    setError('');
    setLoading(true);

    // Validate boards only if subjects were selected (not for University/Independent)
    if (regSubjects.length > 0) {
        const missingBoards = regSubjects.some(s => !regBoards[s]);
        if (missingBoards) {
            setError('Please select an exam board for all subjects.');
            setLoading(false);
            return;
        }
    }

    try {
      const profileData = {
          name: regName.trim(),
          phoneNumber: regPhone,
          level: regLevel || undefined,
          subjects: regSubjects.length > 0 ? regSubjects : undefined,
          examBoards: Object.keys(regBoards).length > 0 ? regBoards : undefined
      };

      let user;
      if (isGoogleOnboarding && googleUser) {
        // Update existing Google user's profile with onboarding data
        const { updateUserProfile } = await import('../services/auth');
        user = await updateUserProfile({
          ...googleUser,
          ...profileData,
        });
      } else {
        // Normal registration
        user = await registerUser(regEmail, regPassword, profileData);
      }

      setCurrentUser(user);
      onLogin(user);
    } catch (err: any) {
      console.error('Registration error:', err);
      const message = err?.message || 'Registration failed';
      if (message.includes('already exists') || message.includes('400')) {
        setError('This email is already registered. Please sign in instead.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const user = await googleLogin();
      // Check if user needs onboarding (new Google user without subjects/level)
      if ((user as any).needsOnboarding) {
        // Start onboarding flow for new Google users
        setGoogleUser(user);
        setRegName(user.name || '');
        setRegEmail(user.email || '');
        setIsGoogleOnboarding(true);
        setIsLogin(false);
        setRegStep(RegStep.Level); // Skip credentials, go straight to level selection
      } else {
        onLogin(user);
      }
    } catch (err: any) {
      // Don't show error for redirect flow
      if (err.message !== 'Redirecting to Google sign-in...') {
        setError(err.message || 'Google Sign-In failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSubject = (s: Subject) => {
      setRegSubjects(prev => 
          prev.includes(s) ? prev.filter(item => item !== s) : [...prev, s]
      );
  };

  const setBoardForSubject = (subj: string, board: string) => {
      setRegBoards(prev => ({...prev, [subj]: board}));
  };

  // --- Render Functions ---

  const renderLogin = () => (
    <form onSubmit={handleLoginSubmit} className="space-y-4 animate-in fade-in duration-300">
        <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Email</label>
        <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
            placeholder="name@example.com"
            required
            />
        </div>
        </div>
        
        <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Password</label>
        <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
            placeholder="••••••••"
            required
            />
        </div>
        </div>

        <button
        type="submit"
        disabled={loading}
        className="w-full bg-cobalt-600 hover:bg-cobalt-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-all flex justify-center items-center"
        >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
        </button>

        <div className="mt-6">
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-900 text-slate-500">Or continue with</span>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
                <button 
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="flex items-center justify-center w-full px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors text-white disabled:opacity-50"
                >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                    />
                    <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                    />
                    <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                    />
                    <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                    />
                </svg>
                Google
                </button>
            </div>
        </div>
    </form>
  );

  const renderRegisterStep = () => {
    switch (regStep) {
        case RegStep.Credentials:
            return (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Your Name <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                            <input
                            type="text"
                            value={regName}
                            onChange={(e) => setRegName(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            placeholder="John Smith"
                            required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Email <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                            <input
                            type="email"
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            placeholder="name@example.com"
                            required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Password <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                            <input
                            type="password"
                            value={regPassword}
                            onChange={(e) => setRegPassword(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            placeholder="••••••••"
                            required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Phone Number (Optional)</label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                            <input
                            type="tel"
                            value={regPhone}
                            onChange={(e) => setRegPhone(e.target.value)}
                            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            placeholder="+44 7700 900000"
                            />
                        </div>
                    </div>

                    {/* Google Sign-Up Option */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-slate-800"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-slate-900 text-slate-500">Or sign up with</span>
                            </div>
                        </div>

                        <div className="mt-4">
                            <button
                            type="button"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            className="flex items-center justify-center w-full px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors text-white disabled:opacity-50"
                            >
                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                                <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                                />
                                <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                                />
                                <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                                />
                                <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                                />
                            </svg>
                            Google
                            </button>
                        </div>
                    </div>
                </div>
            );
        case RegStep.Level:
            return (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                    <p className="text-sm text-slate-400 mb-4">Choose your education level.</p>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setRegLevel('A-Level')}
                            className={`p-5 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center group ${regLevel === 'A-Level' ? 'border-cobalt-500 bg-cobalt-900/20' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
                        >
                            <GraduationCap className={`w-8 h-8 mb-2 ${regLevel === 'A-Level' ? 'text-cobalt-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                            <h3 className="text-base font-bold text-white">A-Level</h3>
                            <p className="text-xs text-slate-400 mt-1">Advanced Level</p>
                        </button>

                        <button
                            type="button"
                            onClick={() => setRegLevel('GCSE')}
                            className={`p-5 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center group ${regLevel === 'GCSE' ? 'border-cobalt-500 bg-cobalt-900/20' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
                        >
                            <BookOpen className={`w-8 h-8 mb-2 ${regLevel === 'GCSE' ? 'text-cobalt-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                            <h3 className="text-base font-bold text-white">GCSE</h3>
                            <p className="text-xs text-slate-400 mt-1">Secondary</p>
                        </button>

                        <button
                            type="button"
                            onClick={() => setRegLevel('University')}
                            className={`p-5 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center group ${regLevel === 'University' ? 'border-cobalt-500 bg-cobalt-900/20' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
                        >
                            <Building2 className={`w-8 h-8 mb-2 ${regLevel === 'University' ? 'text-cobalt-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                            <h3 className="text-base font-bold text-white">University</h3>
                            <p className="text-xs text-slate-400 mt-1">Higher Education</p>
                        </button>

                        <button
                            type="button"
                            onClick={() => setRegLevel('Independent')}
                            className={`p-5 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center group ${regLevel === 'Independent' ? 'border-cobalt-500 bg-cobalt-900/20' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
                        >
                            <Compass className={`w-8 h-8 mb-2 ${regLevel === 'Independent' ? 'text-cobalt-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                            <h3 className="text-base font-bold text-white">Independent</h3>
                            <p className="text-xs text-slate-400 mt-1">Self-directed</p>
                        </button>
                    </div>
                </div>
            );
        case RegStep.Subjects:
            const availableSubjects = regLevel === 'GCSE'
                ? [Subject.GCSEMaths, Subject.GCSEBiology, Subject.GCSEPhysics, Subject.GCSEChemistry, Subject.GCSEHistory]
                : [Subject.Physics, Subject.Chemistry, Subject.Maths, Subject.FurtherMaths, Subject.Biology, Subject.Economics];
            const comingSoonSubjects = regLevel === 'GCSE' ? ['Computer Science', 'English'] : ['Computer Science', 'History'];
            return (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                     <p className="text-sm text-slate-400 mb-4">Select the subjects you are studying.</p>
                     <div className="grid grid-cols-2 gap-3">
                         {availableSubjects.map(s => (
                             <button
                                key={s}
                                type="button"
                                onClick={() => toggleSubject(s)}
                                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${regSubjects.includes(s) ? 'border-cobalt-500 bg-cobalt-600 text-white shadow-lg shadow-cobalt-900/50' : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-500 hover:text-white'}`}
                             >
                                 {s}
                             </button>
                         ))}
                     </div>
                     <div className="mt-4 pt-4 border-t border-slate-800">
                         <p className="text-xs text-slate-500 mb-2 font-bold uppercase tracking-wider">Coming Soon</p>
                         <div className="grid grid-cols-2 gap-3 opacity-50">
                             {comingSoonSubjects.map(s => (
                                 <div key={s} className="p-3 rounded-lg border border-slate-800 bg-slate-950 text-slate-600 text-sm font-medium text-center relative overflow-hidden">
                                     {s}
                                     <div className="absolute inset-0 bg-slate-950/20"></div>
                                 </div>
                             ))}
                         </div>
                     </div>
                </div>
            );
        case RegStep.ExamBoards:
            return (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                    <p className="text-sm text-slate-400">Select your exam board for each subject.</p>
                    
                    {regSubjects.map(subject => (
                        <div key={subject} className="space-y-2">
                            <h3 className="text-white font-medium flex items-center">
                                <span className={`w-2 h-2 rounded-full mr-2 ${subject === Subject.Physics ? 'bg-indigo-500' : subject === Subject.Chemistry ? 'bg-purple-500' : subject === Subject.GCSEMaths ? 'bg-green-500' : subject === Subject.FurtherMaths ? 'bg-orange-500' : 'bg-blue-500'}`}></span>
                                {subject}
                            </h3>
                            <div className="grid grid-cols-3 gap-2">
                                {BOARD_OPTIONS.map(board => {
                                    const isActive = getBoardsForSubject(subject).includes(board);
                                    
                                    return (
                                        <button
                                            key={board}
                                            type="button"
                                            disabled={!isActive}
                                            onClick={() => isActive && setBoardForSubject(subject, board)}
                                            className={`
                                                relative px-3 py-2 rounded-lg text-sm font-bold border-2 transition-all
                                                ${!isActive 
                                                    ? 'border-slate-800 bg-slate-900/50 text-slate-600 cursor-not-allowed' 
                                                    : regBoards[subject] === board 
                                                        ? 'border-cobalt-500 bg-cobalt-900/40 text-white' 
                                                        : 'border-slate-600 bg-slate-800 text-slate-300 hover:border-slate-500'}
                                            `}
                                        >
                                            {board}
                                            {!isActive && <span className="absolute -top-1 -right-1 flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-500 opacity-0"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-slate-700"></span></span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            );
        default:
            return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl transition-all duration-300">
        <div className="flex justify-center mb-8">
          <div className="bg-cobalt-600 p-3 rounded-xl shadow-lg shadow-cobalt-900/50">
            <Atom className="w-8 h-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-center text-white mb-2">
          {isLogin ? 'Welcome back' : 'Create an account'}
        </h2>
        <p className="text-center text-slate-400 mb-8">
          {isLogin 
            ? 'Enter your details to access your dashboard' 
            : regStep === RegStep.Credentials ? 'Start your learning journey today'
            : regStep === RegStep.Level ? 'Select your education level'
            : regStep === RegStep.Subjects ? 'Choose your subjects'
            : 'Finalize your setup'
          }
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 mb-6 flex items-center text-red-400 text-sm animate-in fade-in slide-in-from-top-2">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {isLogin ? renderLogin() : (
            <div className="space-y-6">
                {/* Progress Bar for Registration */}
                <div className="flex gap-2 mb-6">
                    {[0, 1, 2, 3].map(step => (
                        <div key={step} className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step <= regStep ? 'bg-cobalt-500' : 'bg-slate-800'}`} />
                    ))}
                </div>

                {renderRegisterStep()}

                <div className="flex gap-3 pt-4">
                    {regStep > 0 && (
                        <button
                            onClick={handlePrevStep}
                            className="flex-1 py-3 px-4 rounded-lg border border-slate-700 text-slate-300 font-medium hover:bg-slate-800 transition-colors flex items-center justify-center"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            Back
                        </button>
                    )}
                    {/* Show Complete Setup for University/Independent on Level step, or on ExamBoards step */}
                    {(regStep === RegStep.Level && (regLevel === 'University' || regLevel === 'Independent')) || regStep === RegStep.ExamBoards ? (
                        <button
                            onClick={handleRegisterSubmit}
                            disabled={loading || (regStep === RegStep.Level && !regLevel)}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-all flex justify-center items-center shadow-lg shadow-green-900/20"
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Setup'}
                        </button>
                    ) : regStep < RegStep.ExamBoards ? (
                        <button
                            onClick={handleNextStep}
                            className="flex-1 bg-cobalt-600 hover:bg-cobalt-700 text-white font-bold py-3 rounded-lg transition-all flex justify-center items-center shadow-lg shadow-cobalt-900/20"
                        >
                            Next <ChevronRight className="w-4 h-4 ml-2" />
                        </button>
                    ) : null}
                </div>
            </div>
        )}

        <div className="mt-8 text-center border-t border-slate-800 pt-6">
          <p className="text-sm text-slate-400">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button 
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                // Reset registration state on toggle
                if (isLogin) {
                    setRegStep(RegStep.Credentials);
                    setRegName('');
                    setRegEmail('');
                    setRegPassword('');
                    setRegPhone('');
                    setRegLevel(null);
                    setRegSubjects([]);
                    setRegBoards({});
                }
              }}
              className="text-cobalt-400 hover:text-cobalt-300 font-medium transition-colors"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
